import { ResultSet } from "@cubejs-client/core";
import { createAppAsyncThunk } from "appThunk";
import { cubeProductQueryFilterHelper } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import { Partner } from "./partner";
import { ProductSales } from "./productSales";
import { DateTime } from "luxon";

export enum OperatorStatus {
    ExistingOperator = "Existing operator",
    NewOperator = "New operator"
}

export class Operator {
    public readonly id: number;
    public readonly name: string;
    public readonly sales: ProductSales;
    public readonly status: OperatorStatus;
    public readonly partner: Partner;

    constructor(
        id: number,
        name: string,
        clientSourcedSales: number,
        estimatedSales: number,
        optimisedSales: number,
        salesHeadroom: number,
        partner: Partner,
    ) {
        this.id = id;
        this.name = name;
        this.sales = new ProductSales(
            String(id),
            name,
            clientSourcedSales,
            estimatedSales,
            optimisedSales,
            salesHeadroom
        );
        this.status = clientSourcedSales > 0 ? OperatorStatus.ExistingOperator : OperatorStatus.NewOperator;
        this.partner = partner;
    }
}

export const loadFilteredOperators = createAppAsyncThunk<Operator[], { referenceDate: DateTime, candidatePartner?: Partner }>(
    "customer/tools/product/loadFilteredOperators",
    async ({ candidatePartner, referenceDate }, thunkAPI) => {
        try {
            if (!candidatePartner) {
                return [];
            }
            const startDate = referenceDate.minus({ months: 12 }).plus({ days: 1 }).startOf('day');
            const endDate = referenceDate.endOf('day');

            const query = {
                segments: [
                    "D_Store.OpenPhysicalStores"
                ],
                dimensions: [
                    "D_Store.Operator",
                    "D_Store.Group"
                ],
                timeDimensions: [{
                    dimension: "D_Date.Date",
                    dateRange: [startDate, endDate]
                }],
                filters: [{
                    member: "D_Store.Operator",
                    operator: "set"
                }],
                measures: [
                    "ProductSalesEstimate.SumClientSourcedSales",
                    "ProductSalesEstimate.SumEstimatedSales",
                    "ProductSalesEstimate.SumOptimisedSales",
                    "ProductSalesEstimate.SumSalesHeadroom"
                ]
            };

            const resultSet = await thunkAPI.dispatch(
                cubeProductQueryFilterHelper(query, undefined, candidatePartner, undefined, undefined)
            ) as unknown as ResultSet;

            let id = 0;
            return resultSet.rawData().map(row => new Operator(
                id++,
                row["D_Store.Operator"],
                Number(row["ProductSalesEstimate.SumClientSourcedSales"]),
                Number(row["ProductSalesEstimate.SumEstimatedSales"]),
                Number(row["ProductSalesEstimate.SumOptimisedSales"]),
                Number(row["ProductSalesEstimate.SumSalesHeadroom"]),
                row["D_Store.Group"]
            ));
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading Operators.", error));
            throw error;
        }
    }
);
